/** Conservative per-texture memory budget (RGBA bytes) for pipeline selection only. */
export const MAX_SINGLE_TEXTURE_BYTES = 64 * 1024 * 1024;

const CANARY_TEXTURE_SIZE = 1;

export interface WebGLCapabilities {
  supportsFragmentHighp: boolean;
  maxTextureImageUnits: number;
  maxFragmentUniformVectors: number;
  maxViewportDims: [number, number];
  maxTextureSize: number;
  maxRenderbufferSize: number;
}

export interface WebGLFallbackContext {
  operation: string;
  reason: string;
  width?: number;
  height?: number;
  gl?: WebGLRenderingContext;
}

const capabilitiesByContext = new Map<WebGLRenderingContext, WebGLCapabilities>();

export function getWebGLCapabilities(gl: WebGLRenderingContext): WebGLCapabilities {
  const cached = capabilitiesByContext.get(gl);
  if (cached) {
    return cached;
  }

  const highpFormat = gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT);
  const hasFragmentHighp = highpFormat !== null && highpFormat.precision > 0;
  const maxViewport = gl.getParameter(gl.MAX_VIEWPORT_DIMS) as Int32Array;

  const capabilities: WebGLCapabilities = {
    supportsFragmentHighp: hasFragmentHighp,
    maxTextureImageUnits: gl.getParameter(gl.MAX_TEXTURE_IMAGE_UNITS) as number,
    maxFragmentUniformVectors: gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS) as number,
    maxViewportDims: [maxViewport[0], maxViewport[1]],
    maxTextureSize: gl.getParameter(gl.MAX_TEXTURE_SIZE) as number,
    maxRenderbufferSize: gl.getParameter(gl.MAX_RENDERBUFFER_SIZE) as number
  };

  capabilitiesByContext.set(gl, capabilities);
  return capabilities;
}

export function clearWebGLCapabilitiesCache(): void {
  capabilitiesByContext.clear();
}

/**
 * WebGL 1 requires power-of-two dimensions when using gl.REPEAT.
 */
export function canUseRepeatWrapForTexture(gl: WebGLRenderingContext, width: number, height: number): boolean {
  const isWebGL1 = typeof WebGL2RenderingContext === 'undefined' || !(gl instanceof WebGL2RenderingContext);
  if (!isWebGL1) {
    return true;
  }

  return isPowerOfTwo(width) && isPowerOfTwo(height);
}

/**
 * Whether the context can render to an offscreen target at the requested size.
 * Runs a 1x1 canary probe first, then checks device limits and performs a full-size probe when needed.
 */
export function validateWebGLPipeline(gl: WebGLRenderingContext, width: number, height: number): boolean {
  if (!probeColorAttachmentFramebuffer(gl, CANARY_TEXTURE_SIZE, CANARY_TEXTURE_SIZE)) {
    return false;
  }

  if (width <= 0 || height <= 0) {
    return true;
  }

  if (!fitsRenderTarget(gl, width, height)) {
    return false;
  }

  return probeColorAttachmentFramebuffer(gl, width, height);
}

export function logWebGLFallback(context: WebGLFallbackContext): void {
  const parts = [
    `StaticPipelineOperations: ${context.operation} falling back to Canvas2D (${context.reason}).`
  ];

  if (context.width !== undefined && context.height !== undefined) {
    parts.push(`Dimensions: ${context.width}x${context.height}.`);
  }

  if (context.gl) {
    const debugInfo = context.gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const vendor = context.gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) as string;
      const renderer = context.gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) as string;
      parts.push(`GPU: ${vendor} / ${renderer}.`);
    }
    parts.push(`GL version: ${context.gl.getParameter(context.gl.VERSION) as string}.`);
  }

  console.warn(parts.join(' '));
}

const WEBGL_PIPELINE_ERROR_MARKERS = [
  'pipelineglutils',
  'webgl',
  'gl context',
  'frame buffer',
  'framebuffer',
  'shader',
  'linkstatus',
  'uniform',
  'texture',
  'readpixels',
  'attribute'
] as const;

export function isWebGLPipelineError(error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false;
  }

  const message = error.message.toLowerCase();
  return WEBGL_PIPELINE_ERROR_MARKERS.some((marker) => message.includes(marker));
}

function isPowerOfTwo(value: number): boolean {
  return value > 0 && (value & (value - 1)) === 0;
}

function fitsRenderTarget(gl: WebGLRenderingContext, width: number, height: number): boolean {
  if (width <= 0 || height <= 0) {
    return false;
  }

  const { maxTextureSize, maxRenderbufferSize, maxViewportDims } = getWebGLCapabilities(gl);
  const [maxViewportW, maxViewportH] = maxViewportDims;

  return width <= maxTextureSize
    && height <= maxTextureSize
    && width <= maxRenderbufferSize
    && height <= maxRenderbufferSize
    && width <= maxViewportW
    && height <= maxViewportH
    && width * height * 4 <= MAX_SINGLE_TEXTURE_BYTES;
}

function probeColorAttachmentFramebuffer(gl: WebGLRenderingContext, width: number, height: number): boolean {
  if (gl.isContextLost()) {
    return false;
  }

  while (gl.getError() !== gl.NO_ERROR) {
    // drain stale errors before probing
  }

  const texture = gl.createTexture();
  if (!texture) {
    return false;
  }

  gl.bindTexture(gl.TEXTURE_2D, texture);

  try {
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);

    if (gl.getError() !== gl.NO_ERROR || gl.isContextLost()) {
      return false;
    }

    const framebuffer = gl.createFramebuffer();
    if (!framebuffer) {
      return false;
    }

    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

    try {
      gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);

      return gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE
        && gl.getError() === gl.NO_ERROR
        && !gl.isContextLost();
    }
    finally {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(framebuffer);
    }
  }
  finally {
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.deleteTexture(texture);
  }
}
